import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle, Linkedin, Github } from 'lucide-react';
import { Section, Container, SectionHead } from './Section';
import { personal } from '../data/portfolio';

const EMAILJS_CONFIG = {
  service_id: 'default_service',
  template_id: 'template_7ulrzmq',
  user_id: 'user_L05xsaOfp1IVVTrxAiTaU',
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  align-items: stretch;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 0.9fr 1.1fr;
    gap: 40px;
  }
`;

const Info = styled(motion.div)`
  padding: 32px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};

  h3 { font-size: 26px; margin-bottom: 12px; }
  p {
    color: ${({ theme }) => theme.colors.textDim};
    margin-bottom: 24px;
    line-height: 1.7;
  }
`;

const InfoItem = styled.a`
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radii.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 12px;
  transition: background 200ms ease, border-color 200ms ease, transform 200ms ease;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHi};
    border-color: ${({ theme }) => theme.colors.primarySoft};
    transform: translateX(4px);
  }

  .icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: rgba(124,92,255,0.15);
    border: 1px solid rgba(124,92,255,0.25);
    color: ${({ theme }) => theme.colors.primarySoft};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .label {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    font-family: ${({ theme }) => theme.fonts.mono};
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
  .value { color: ${({ theme }) => theme.colors.text}; font-size: 15px; margin-top: 2px; }
`;

const Socials = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 22px;
`;

const Social = styled.a`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textDim};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 200ms ease, border-color 200ms ease, transform 200ms ease;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primarySoft};
    border-color: ${({ theme }) => theme.colors.primarySoft};
    transform: translateY(-2px);
  }
`;

const Form = styled(motion.form)`
  padding: 32px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  display: grid;
  gap: 16px;
`;

const Field = styled.label`
  display: block;

  span {
    display: block;
    font-size: 12px;
    font-family: ${({ theme }) => theme.fonts.mono};
    color: ${({ theme }) => theme.colors.textMuted};
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  input, textarea {
    width: 100%;
    padding: 14px 16px;
    background: ${({ theme }) => theme.colors.bg};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.radii.md};
    font-family: inherit;
    font-size: 15px;
    transition: border-color 200ms ease, box-shadow 200ms ease;
    outline: none;
  }
  textarea { min-height: 140px; resize: vertical; }

  input:focus, textarea:focus {
    border-color: ${({ theme }) => theme.colors.primarySoft};
    box-shadow: 0 0 0 4px rgba(124,92,255,0.12);
  }
`;

const Submit = styled.button`
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: 500;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 999px;
  cursor: pointer;
  transition: transform 200ms ease, background 200ms ease;

  &:hover { transform: translateY(-2px); background: #6947f5; }
  &:disabled { opacity: 0.7; cursor: default; transform: none; }
`;

const Success = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.success};
  font-size: 14px;
`;

const ErrorMsg = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 14px;
`;

export default function Contact() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    setErrorMsg('');
    setSent(false);

    const params = {
      service_id: EMAILJS_CONFIG.service_id,
      template_id: EMAILJS_CONFIG.template_id,
      user_id: EMAILJS_CONFIG.user_id,
      template_params: {
        name: form.name,
        email: form.email,
        message: form.message,
        from_name: form.name,
        from_email: form.email,
        reply_to: form.email,
        'g-recaptcha-response': '03AHJ_ASjnLA214KSNKFJAK12sfKASfehbmfd...',
      },
    };

    try {
      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      if (response.status === 200 || response.status === 201) {
        setSent(true);
        setForm({ name: '', email: '', message: '' });
        setTimeout(() => setSent(false), 5000);
      } else {
        const text = await response.text().catch(() => '');
        console.error('EmailJS error:', response.status, text);
        setErrorMsg("Couldn't send message. Please try again or email me directly.");
      }
    } catch (err) {
      console.error('EmailJS network error:', err);
      setErrorMsg("Network error. Please try again or email me directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="contact" ref={ref}>
      <Container>
        <SectionHead kicker="Contact Me" title="Let's Build" gradient="Something Great" sub="Have a project in mind? Send a note, I'll get back to you within 24 hours." />
        <Grid>
          <Info initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6 }}>
            <h3>Get in touch</h3>
            <p>The fastest way to start a conversation. Prefer email? A message drops straight into my inbox.</p>

            <InfoItem href={`mailto:${personal.email}`}>
              <span className="icon"><Mail size={18} /></span>
              <div><div className="label">Email</div><div className="value">{personal.email}</div></div>
            </InfoItem>
            <InfoItem href={`tel:${personal.phone.replace(/\s/g, '')}`}>
              <span className="icon"><Phone size={18} /></span>
              <div><div className="label">Phone</div><div className="value">{personal.phone}</div></div>
            </InfoItem>
            <InfoItem as="div">
              <span className="icon"><MapPin size={18} /></span>
              <div><div className="label">Location</div><div className="value">{personal.location}</div></div>
            </InfoItem>

            <Socials>
              <Social href={personal.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin size={18} /></Social>
              <Social href={personal.socials.github} target="_blank" rel="noreferrer" aria-label="GitHub"><Github size={18} /></Social>
            </Socials>
          </Info>

          <Form onSubmit={onSubmit} initial={{ opacity: 0, x: 20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.1 }}>
            <Field>
              <span>Your Name</span>
              <input name="name" value={form.name} onChange={onChange} placeholder="Jane Doe" required />
            </Field>
            <Field>
              <span>Your Email</span>
              <input name="email" type="email" value={form.email} onChange={onChange} placeholder="jane@company.com" required />
            </Field>
            <Field>
              <span>Message</span>
              <textarea name="message" value={form.message} onChange={onChange} placeholder="Tell me about your project…" required />
            </Field>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Submit type="submit" disabled={loading}>
                {loading ? 'Sending…' : (<>Send Message <Send size={16} /></>)}
              </Submit>
              {sent && <Success><CheckCircle2 size={16} /> Message sent—I'll be in touch soon.</Success>}
              {errorMsg && <ErrorMsg><AlertCircle size={16} /> {errorMsg}</ErrorMsg>}
            </div>
          </Form>
        </Grid>
      </Container>
    </Section>
  );
}
